import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomPopover from "src/components/CustomPopover";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import {
  delete_feed_action,
  userFeedGroupListing,
  userFeedListing,
} from "src/DAL/Community/Community";
import CreatePost from "./CreatePost";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrCard from "src/design-system/components/GrCard";
import GrSearch from "src/design-system/components/GrSearch";
import GrDataTable from "src/design-system/components/GrDataTable";
import GrTablePagination from "src/design-system/components/GrTablePagination";
import GrStatusPill from "src/design-system/components/GrStatusPill";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrIcon from "src/design-system/components/GrIcon";

// My Posts (/my-posts, and a group's posts when opened with the group as router state): the member's
// own posts with their publish status (Rejected shows the reason on hover or tap), search, Edit / Re-Submit
// and Delete from the ⋮ menu (Edit opens the same Create post popup).
const ROWS_PER_PAGE_OPTIONS = [50, 100, 150];

const STATUS = {
  approved: { pill: "ok", text: "Approved" },
  rejected: { pill: "overdue", text: "Rejected" },
  pending: { pill: "due", text: "Pending" },
};

// Search sits above the table on the right (full width on phones).
const toolbarSx = { display: "flex", justifyContent: "flex-end", mb: gr.space[4] };
const searchSx = { width: { xs: "100%", sm: `calc(${gr.space[12]} * 8)` } };
const descriptionSx = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontWeight: gr.font.weight.medium,
};
// A photo post without text gets a quiet label instead of an empty cell (wording OK'd by Mohsin).
const PHOTO_POST_LABEL = "Photo post";
const placeholderSx = {
  ...descriptionSx,
  color: gr.color.muted,
  fontWeight: gr.font.weight.regular,
};
const loaderSx = {
  minHeight: `calc(${gr.space[12]} * 5)`,
  alignItems: "center",
};

// The member's search: posts whose description contains the text (case-insensitive).
const matchesSearch = (post, query) =>
  post?.description?.toLowerCase().indexOf(query.toLowerCase().trim()) !== -1;

export default function MyPosts() {
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [userList, setUserList] = useState([]);
  // All my posts on the server (the API sends one page at a time), for the pager.
  const [totalPosts, setTotalPosts] = useState(0);
  // Loading from the first frame (the list loads on mount), so "No results found" never flashes first.
  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDoc, setDeleteDoc] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [feedType, setFeedType] = useState("general");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const navigate = useNavigate();
  const [selectedFeed, setSelectedFeed] = useState({});
  const [formAction, setFormAction] = useState("ADD");
  const { state } = useLocation();
  // A group's posts when opened with the group as router state (group page, rejected-post notification).
  const group = state?._id;
  const { userInfo } = useContentSetting();

  const getFormListing = async () => {
    setIsLoading(true);
    const result = group
      ? await userFeedGroupListing(page, rowsPerPage, "", group)
      : await userFeedListing(page, rowsPerPage, "");
    if (result.code === 200) {
      setUserList(result.feeds);
      setTotalPosts(result.total_feeds ?? result.feeds.length);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleEdit = (value) => {
    setSelectedFeed(value);
    setOpenCreateModal(true);
    setFormAction("EDIT");
  };
  const handleAgreeDelete = (value) => {
    setDeleteDoc(value);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    setOpenDelete(false);
    setIsLoading(true);
    const result = await delete_feed_action(deleteDoc._id);
    if (result.code === 200) {
      enqueueSnackbar("Post deleted successfully", { variant: "success" });
      getFormListing();
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  // The API sends only the current page, so the rows are not sliced again (page 2 used to be empty).
  const filteredPosts = filterName
    ? userList.filter((post) => matchesSearch(post, filterName))
    : userList;

  useEffect(() => {
    getFormListing();
  }, [page, rowsPerPage]);

  const MENU_OPTIONS2 = [
    {
      label: "Edit",
      icon: icons.edit,
      handleClick: handleEdit,
    },
    {
      label: "Delete",
      icon: icons.deleteTwoTone,
      danger: true,
      handleClick: handleAgreeDelete,
    },
  ];
  const MENU_OPTIONS_NEW = [
    {
      label: "Re-Submit",
      icon: icons.edit,
      handleClick: handleEdit,
    },
    {
      label: "Delete",
      icon: icons.deleteTwoTone,
      danger: true,
      handleClick: handleAgreeDelete,
    },
  ];

  const columns = [
    {
      key: "number",
      label: "#",
      width: gr.space[12],
      render: (row, index) => page * rowsPerPage + (index + 1),
    },
    {
      key: "description",
      label: "Description",
      primary: true,
      width: "minmax(0, 3fr)",
      render: (row) =>
        row.description || row.feed_type !== "image" ? (
          <Box component="span" sx={descriptionSx} title={row.description}>
            {row.description}
          </Box>
        ) : (
          <Box component="span" sx={placeholderSx}>
            {PHOTO_POST_LABEL}
          </Box>
        ),
    },
    {
      key: "publish_status",
      label: "Publish Status",
      render: (row) => {
        const status = STATUS[row.publish_status] || STATUS.pending;
        return (
          <GrStatusPill
            status={status.pill}
            hint={
              row.publish_status === "rejected"
                ? row.rejection_reason
                : undefined
            }
          >
            {status.text}
          </GrStatusPill>
        );
      },
    },
    {
      key: "createdAt",
      label: "Date",
      render: (row) => moment(row.createdAt).format("ddd, MMM DD, YYYY"),
    },
    {
      key: "action",
      label: "Action",
      action: true,
      showLabel: true,
      align: "right",
      // Room for the "Action" heading; the ⋮ button sits at the right edge under it.
      width: `calc(${gr.control.md} + ${gr.space[6]})`,
      render: (row) => (
        <CustomPopover
          menu={
            row.publish_status === "rejected" ? MENU_OPTIONS_NEW : MENU_OPTIONS2
          }
          data={row}
        />
      ),
    },
  ];

  const content = (
    <>
      <GrCard as="div">
        <Box sx={toolbarSx}>
          <GrSearch
            value={filterName}
            onChange={handleFilterByName}
            onClear={() => setFilterName("")}
            sx={searchSx}
          />
        </Box>
        {isLoading ? (
          // While loading: the headings and the rows' space, so the card does not pop in and jump.
          <>
            <GrDataTable ariaLabel="My Posts" columns={columns} rows={[]} />
            <GrLoader sx={loaderSx} />
          </>
        ) : filteredPosts.length ? (
          <GrDataTable
            ariaLabel="My Posts"
            columns={columns}
            rows={filteredPosts}
            getRowKey={(row, index) => row._id || index}
          />
        ) : (
          // No posts: the headings stay, so the member still sees what the list holds.
          <>
            <GrDataTable ariaLabel="My Posts" columns={columns} rows={[]} />
            <GrEmptyState
              compact
              icon={<GrIcon icon={icons.search} />}
              title="No results found"
            />
          </>
        )}
        <GrTablePagination
          count={totalPosts}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </GrCard>
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete ?"}
        handleAgree={handleDelete}
      />
      <CreatePost
        setFeedType={setFeedType}
        feedType={feedType}
        openCreateModal={openCreateModal}
        setOpenCreateModal={setOpenCreateModal}
        getFeedsListing={getFormListing}
        userInfo={userInfo}
        selectedFeed={selectedFeed}
        formAction={formAction}
        setFormAction={setFormAction}
      />
    </>
  );

  return (
    <GrPage sx={{ pb: gr.space[8] }}>
      <GrPageHeader title="My Posts" onBack={() => navigate(-1)} />
      {content}
    </GrPage>
  );
}
